import React, { useState } from 'react';
import { ClockIcon } from '../chat/SidebarIcons';
import { formatEstimateTime } from '../../utils/estimateTime';
import QuickTaskComposer from './QuickTaskComposer';
import WorkItemTimer from '../common/WorkItemTimer';

export const TaskEstimateBadge = ({ minutes, title = 'Estimated time' }) => {
  const label = formatEstimateTime(minutes);
  if (!label) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 flex-shrink-0 text-[11px] font-medium text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap"
      title={title}
    >
      <ClockIcon className="w-3 h-3" />
      {label}
    </span>
  );
};

const hoverIconClass =
  'flex-shrink-0 p-1 rounded-md text-gray-400 opacity-0 pointer-events-none group-hover/sub:opacity-100 group-hover/sub:pointer-events-auto focus:opacity-100 focus:pointer-events-auto hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200';

/**
 * Named subtask row: title, estimate time, and delete — always one line.
 */
const TaskSubtaskItem = ({
  subtask,
  busy = false,
  readOnly = false,
  indent = true,
  onEdit,
  onEditTime,
  onDelete,
  onClone,
  onComplete,
  onOpen,
  assigneeLabel = '',
  timerDisabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const title = (subtask?.title || '').trim() || 'Subtask';
  const finishedMinutes = Number(subtask?.completionTimeInMinutes);
  const finished = Number.isFinite(finishedMinutes) && finishedMinutes > 0;

  if (!readOnly && editing) {
    return (
      <div className="w-full min-w-0">
        <QuickTaskComposer
          compact
          flush={!indent}
          placeholder="Subtask title"
          submitLabel="Save"
          saving={busy}
          initialTitle={subtask?.title || ''}
          initialMinutes={subtask?.estimateTimeInMinutes || 15}
          onSubmit={async (payload) => {
            await onEdit?.(payload);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className={`group/sub flex items-center gap-2 w-full min-w-0 min-h-8 py-1 ${
      indent ? 'px-3.5 pl-12' : 'px-3'
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
        finished ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-500'
      }`} />
      <span
        className={`min-w-0 flex-1 truncate text-[12px] leading-none ${
          finished
            ? 'text-gray-400 dark:text-gray-500 line-through'
            : 'text-gray-700 dark:text-gray-200'
        } ${onOpen ? 'cursor-pointer' : ''}`}
        title={title}
        onClick={onOpen ? (e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        } : undefined}
      >
        {title}
      </span>
      {Array.isArray(subtask?.tags) && subtask.tags.some((tag) => String(tag).trim().toLowerCase() === 'cloned') ? (
        <span className="flex-shrink-0 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-violet-700 dark:border-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
          cloned
        </span>
      ) : null}
      {!readOnly && !finished && onEdit && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setEditing(true);
          }}
          className={hoverIconClass}
          title="Rename"
          aria-label="Rename"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 11l6.232-6.232a2.5 2.5 0 113.536 3.536L12.536 14.536a2 2 0 01-.878.513l-3.09.883a.5.5 0 01-.62-.62l.883-3.09a2 2 0 01.513-.878z" />
          </svg>
        </button>
      )}
      {assigneeLabel ? (
        <span
          className="flex-shrink-0 max-w-[6.5rem] truncate text-[10px] font-medium text-gray-400 dark:text-gray-500"
          title={`Assigned to ${assigneeLabel}`}
        >
          {assigneeLabel}
        </span>
      ) : null}
      <TaskEstimateBadge
        minutes={finished ? finishedMinutes : subtask?.estimateTimeInMinutes}
        title={finished ? 'Time spent' : 'Estimated time'}
      />
      {finished && onEditTime && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onEditTime();
          }}
          className={`${hoverIconClass} opacity-100 pointer-events-auto`}
          title="Edit time spent"
          aria-label="Edit time spent"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      )}
      {!finished && (
        <WorkItemTimer
          targetType="TASK"
          targetId={subtask?.id}
          noun="subtask"
          compact
          disabled={timerDisabled || (readOnly && !onComplete)}
        />
      )}
      {onOpen && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpen();
          }}
          className="flex-shrink-0 p-1 rounded-md text-primary-500 hover:text-primary-700 hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-gray-700"
          title="Open task"
          aria-label="Open task"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h6m0 0v6m0-6L10 16" />
          </svg>
        </button>
      )}
      {!readOnly && !finished && onComplete && (
        <button
          type="button"
          onClick={() => onComplete()}
          disabled={busy}
          className={`${hoverIconClass} hover:text-emerald-600 dark:hover:text-emerald-300`}
          title="Finish subtask"
          aria-label="Finish subtask"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </button>
      )}
      {!readOnly && onClone && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClone();
          }}
          disabled={busy}
          className={`${hoverIconClass} hover:text-primary-600 dark:hover:text-primary-300`}
          title="Clone task"
          aria-label="Clone task"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2M8 7V5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2h-2M8 7h8a2 2 0 012 2v2" />
          </svg>
        </button>
      )}
      {!readOnly && onDelete && (
        <button
          type="button"
          onClick={() => onDelete()}
          disabled={busy}
          className={`${hoverIconClass} hover:text-rose-600 dark:hover:text-rose-300`}
          title="Delete subtask"
          aria-label="Delete subtask"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default TaskSubtaskItem;
